import React, { useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';

const AddSessionModal = ({ exercises, onAdd, onClose }) => {
  const [exerciseId, setExerciseId] = useState(exercises[0]?.id || '');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [warmup, setWarmup] = useState('');
  const [sets, setSets] = useState([{ weight: '', reps: '' }]);
  const [duration, setDuration] = useState('');
  const [distance, setDistance] = useState('');

  const exercise = exercises.find(e => e.id === Number(exerciseId));

  const calculatedAvgSpeed = exercise?.avgSpeedMetric && duration && distance 
    ? ((parseFloat(distance) / parseFloat(duration)) * 60).toFixed(2)
    : '';

  const addSet = () => {
    setSets([...sets, { weight: '', reps: '' }]);
  };

  const updateSet = (index, field, value) => {
    const newSets = [...sets];
    newSets[index][field] = value;
    setSets(newSets);
  };

  const removeSet = (index) => {
    setSets(sets.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const session = {
      exerciseId: Number(exerciseId),
      date,
      warmup: warmup || undefined,
      sets: exercise?.type === 'strength' ? sets.filter(s => s.weight && s.reps) : undefined,
      avgSpeed: exercise?.avgSpeedMetric ? calculatedAvgSpeed : undefined,
      duration: duration || undefined,
      distance: exercise?.type !== 'strength' ? distance : undefined
    };
    onAdd(session);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Add Training Session</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-300">Exercise</label>
            <select
              value={exerciseId}
              onChange={(e) => setExerciseId(e.target.value)}
              required
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {exercises.map(ex => (
                <option key={ex.id} value={ex.id}>{ex.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-300">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-300">Warmup (optional)</label>
            <input
              type="text"
              value={warmup}
              onChange={(e) => setWarmup(e.target.value)}
              placeholder="e.g., 10 min jogging, dynamic stretches"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {exercise?.type === 'strength' && (
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Sets</label>
              <div className="space-y-3">
                {sets.map((set, idx) => (
                  <div key={idx} className="flex gap-3 items-center bg-slate-700/50 p-3 rounded-lg">
                    <span className="text-sm text-slate-400 w-12">Set {idx + 1}</span>
                    <input
                      type="number"
                      placeholder="Weight (kg)"
                      value={set.weight}
                      onChange={(e) => updateSet(idx, 'weight', e.target.value)}
                      className="flex-1 px-3 py-2 bg-slate-600 border border-slate-500 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Reps"
                      value={set.reps}
                      onChange={(e) => updateSet(idx, 'reps', e.target.value)}
                      className="flex-1 px-3 py-2 bg-slate-600 border border-slate-500 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {sets.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSet(idx)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addSet}
                className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-all flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Set
              </button>
            </div>
          )}

          {exercise?.avgSpeedMetric && (
            <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
              <div className="text-sm text-blue-300 mb-1">Average Speed (calculated)</div>
              <div className="text-2xl font-bold text-blue-400">
                {calculatedAvgSpeed || '—'} km/h
              </div>
              <div className="text-xs text-slate-400 mt-1">
                Based on duration and distance
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Duration (min)</label>
              <input
                type="number"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="e.g., 45"
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {exercise?.type !== 'strength' && (
              <div>
                <label className="block text-sm font-semibold mb-2 text-slate-300">Distance (km)</label>
                <input
                  type="number"
                  step="0.1"
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                  placeholder="e.g., 5.2"
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg font-semibold transition-all shadow-lg shadow-green-500/50"
            >
              Add Session
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-semibold transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSessionModal;
